<template>
  <div class="ssml-editor-wrapper">
    <div class="toolbar-wrapper" ref="toolbarWrapper">
      <toolbar 
        ref="toolbar"
        :tools="toolbarConfig"
        @tool-click="handleToolClick"
      />
    </div>
    <editor
      ref="editor"
      :max-length="maxLength"
      :default-voice="defaultVoice"
    />
    <!-- 注册弹窗 -->
    <digits-plugin 
      ref="digitsPlugin"
      @confirm="handleDigitsConfirm"
    />
    <voice-plugin
      ref="voicePlugin"
      :voice-list="voiceList"
      @confirm="handleVoiceConfirm"
    />
  </div>
</template>

<script>
import { Message } from 'element-ui'
import Editor from './Editor.vue'
import Toolbar from './Toolbar.vue'
import { toolbarConfig } from '../constants/toolbar'
import { createSsmlEditorStore } from '../store/createStore'
import Vuex from 'vuex'
import { 
  DigitsPlugin,
  VoicePlugin
} from './plugins'
import { DIGIT_TYPES } from '../constants/ssml'

export default {
  name: 'SsmlEditorWrapper',

  components: {
    Editor,
    Toolbar,
    DigitsPlugin,
    VoicePlugin
  },

  props: {
    maxLength: {
      type: Number,
      default: 20000
    },
    defaultVoice: {
      type: String,
      default: ''
    },
    voiceList: {
      type: Array,
      default: () => []
    }
  },

  data() {
    const store = new Vuex.Store({
      modules: {
        ssmlEditor: createSsmlEditorStore()
      }
    })

    return {
      localStore: store,
      toolbarConfig,
      speedTriggerEl: null
    }
  },

  provide() {
    return {
      store: this.localStore
    }
  },

  mounted() {
    // 确保工具栏wrapper存在
    this.$nextTick(() => {
      if (this.$refs.toolbarWrapper) {
        const speedTrigger = this.$refs.toolbarWrapper.querySelector('[data-tool="speed"]')
        if (speedTrigger) {
          this.speedTriggerEl = speedTrigger
        }
      }
    })
  },

  methods: {
    handleToolClick(tool) {
      const editor = this.$refs.editor
      const savedRange = this.localStore.state.ssmlEditor.savedRange
      
      if (tool.needFocus && !this.localStore.state.ssmlEditor.isFocusInsideEditor) {
        Message.warning('请将光标移至编辑器内')
        return
      }

      if (!savedRange && tool.needSelection) {
        Message.warning('请先选择要操作的文本')
        return
      }

      switch(tool.name) {
        case 'multiWord': {
          if (savedRange && editor.$refs.editableContent.contains(savedRange.commonAncestorContainer)) {
            const selectedText = savedRange.toString().trim()
            
            if (!selectedText) {
              Message.warning('请滑选单个文字')
              return
            }
            
            const reg = /^[\u4e00-\u9fa5]$/
            if (!reg.test(selectedText)) {
              Message.warning('请滑选单个中文字符')
              return
            }
            
            editor.showPhonemeControls(savedRange)
          } else {
            Message.warning('请将光标移至编辑器内')
          }
          break
        }
        
        case 'digits': {
          if (!savedRange) {
            Message.warning('选中数字文本后选择读法')
            return
          }

          const selectedText = savedRange.toString().trim()
          if (!selectedText) {
            Message.warning('选中数字文本后选择读法')
            return
          }

          // 检查选中文本是否包含数字
          if (!/\d/.test(selectedText)) {
            Message.warning('选中的文本须包含数字')
            return
          }

          this.$refs.digitsPlugin.show(savedRange)
          break
        }
        
        case 'pause': {
          editor.insertTag('break', {
            time: tool.data.attrs.time,
            text: tool.data.text
          })
          break
        }

        case 'recycle': {
          editor.insertTag('recycle', {
            text: '连续'
          })
          break
        }

        case 'speed': {
          if (savedRange && editor.$refs.editableContent.contains(savedRange.commonAncestorContainer)) {
            const selectedText = savedRange.toString().trim()
            
            if (!selectedText) {
              Message.warning('请先选择要操作的文本')
              return
            }
            editor.showSpeedControls(savedRange)
          }
          break
        }

        case 'tone': {
          if (savedRange && editor.$refs.editableContent.contains(savedRange.commonAncestorContainer)) {
            const selectedText = savedRange.toString().trim()
            
            if (!selectedText) {
              Message.warning('请先选择要操作的文本')
              return
            }
            editor.showToneControls(savedRange)
          }
          break
        }

        case 'voice': {
          if (savedRange && editor.$refs.editableContent.contains(savedRange.commonAncestorContainer)) {
            const selectedText = savedRange.toString().trim()
            
            if (!selectedText) {
              Message.warning('请先选择要操作的文本')
              return
            }
            this.$refs.voicePlugin.show(savedRange, this.voiceList)
          }
          break
        }

        case 'reset': {
          this.reset()
          break
        }
      }
    },

    handleDigitsConfirm(data) {
      const editor = this.$refs.editor
      const selectedText = data.range.toString()
      // 直接使用传入的 interpret-as 值作为键获取对应的配置
      const typeConfig = DIGIT_TYPES[data.attrs['interpret-as']]
      
      editor.insertTag('say-as', {
        ...data.attrs,
        'display-type': typeConfig.label
      }, `${selectedText}`, data.range)
    },

    handleVoiceConfirm(data) {
      const editor = this.$refs.editor
      const selectedText = data.range.toString()
      
      editor.insertTag('voice', {
        ...data.attrs,
        'display-type': data.attrs['display-type']
      }, selectedText, data.range)
    },

    reset() {
      const editableContent = this.$refs.editor.$refs.editableContent

      // 递归删除所有wrap标签
      const deleteAllWraps = () => {
        const wraps = editableContent.querySelectorAll('[contenteditable="false"]')
        if (wraps.length === 0) return

        wraps.forEach(wrap => {
          const deleteBtn = wrap.querySelector('.delete-btn')
          if (deleteBtn) deleteBtn.click()
        })

        setTimeout(deleteAllWraps, 0)
      }
      deleteAllWraps()

      // 删除插入类标签（break/recycle）
      const insertTags = ['break-wrap', 'recycle-wrap']
      insertTags.forEach(tag => {
        const elements = editableContent.getElementsByTagName(tag)
        while (elements.length > 0) elements[0].remove()
      })
    },
    getFinalSSML() {
      return this.$refs.editor.getFinalSSML()
    },
    splitSSMLByVoiceAndSpeed(ssml, defaultSpeakerId) {
      return this.$refs.editor.splitSSMLByVoiceAndSpeed(ssml, defaultSpeakerId)
    },
    setContent(content, showContent) {
      this.$refs.editor.setContent(content, showContent)
    },
    mergeTTSArrayToEditorHtml(ttsArray, mainSpeakerId, speakerMap) {
      return this.$refs.editor.mergeTTSArrayToEditorHtml(ttsArray, mainSpeakerId, speakerMap)
    },
    getContent() {
      return this.$refs.editor.getContent()
    }
  },

  computed: {
    isFocusInsideEditor() {
      return this.localStore.state.ssmlEditor.isFocusInsideEditor
    }
  },

  watch: {
    defaultVoice: {
      handler(newVoice) {
        if (this.localStore) {
          this.localStore.commit('ssmlEditor/SET_DEFAULT_VOICE', newVoice)
        }
      },
      immediate: true
    }
  }
}
</script>

<style lang="less" scoped>
.ssml-editor-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #FCFEFF;
  box-shadow: 4px 4px 10px 0px rgba(5, 10, 15, 0.05);
  border-radius: 4px 4px 0px 0px;
}
</style>